/**
 * BrandSignatureSetup — tab "Brand para assinatura" do drawer Empresa (U18).
 *
 * Quatro seções:
 *   1. Status da identidade — checklist read-only com 4 critérios usados pela
 *      fn_tenant_signature_ready: nome / tenant.logo_url (header claro,
 *      provisionado pela equipe Joshua) / brand_color / signature_logo
 *      (preto-transparente para PDFs).
 *   2. Logo para header (claro) — preview read-only do tenant.logo_url. Branco
 *      transparente, provisionado pela equipe Joshua para uso em superfícies
 *      escuras (botão Assinar, header da página pública). Renderizado sobre
 *      brand_color para refletir o contexto real.
 *   3. Logo para documentos (PDF) — EDITÁVEL. Reusa Avatar com bucket
 *      'documento' + papel='signature_logo'. Preto/escuro em fundo
 *      transparente, para uso nas páginas brancas de proposta e contrato.
 *      O PDF stamper (U13 assinatura-stamping Edge Function) lê via
 *      service_role no tempo de carimbo.
 *   4. Cor da marca — HEX sólido editável. Preview WCAG vs branco (T4.3).
 *
 * As seções 1, 2 e 4 leem do parent (formData + tenantConfig). A seção 3
 * carrega o entidade_file via Supabase direto (mesmo pattern de Empresa.tsx
 * para o logo da aba "Dados").
 */

import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Check, X, AlertCircle, Info, Upload, Loader2, Trash2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { getErrorMessage } from '@/utils/errorMessageTranslator';
// FieldsetCard is a JS file whose inferred prop types mark optional params as required;
// cast to a permissive FC to avoid TS2740 until FieldsetCard.jsx is migrated.
import _FieldsetCard from '@/components/ui/FieldsetCard';
const FieldsetCard = _FieldsetCard as React.FC<{
  title?: string;
  shadow?: string;
  style?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  children?: React.ReactNode;
}>;
import { DRAWER_STYLES, DRAWER_COMPONENTS } from '@/components/ui/GenericDrawer';
import Switch from '@/components/ui/Switch';

// ─── Constants ──────────────────────────────────────────────────────────────

/** Regex enforcing the same shape `fn_tenant_signature_ready` validates DB-side. */
const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/;

/** WCAG 2.1 AA threshold for normal text (the "Assinar" button label). */
const WCAG_AA_NORMAL = 4.5;

/**
 * The signature button always renders white text on the brand background
 * (locked in the @fibonaccitec/assinatura package), so the contrast that
 * matters is always brand-vs-white.
 */
const SIGNATURE_BUTTON_TEXT_COLOR = '#FFFFFF';

// ─── Props ──────────────────────────────────────────────────────────────────

interface BrandSignatureSetupProps {
  isEditing: boolean;
  /** Tenant id — required by the signature_logo upload widget. */
  tenantId: string;
  formData: { nome?: string | null; nome_fantasia?: string | null; logo_url?: string | null };
  tenantConfig: Record<string, unknown>;
  setTenantConfig: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
}

// ─── WCAG contrast helper ───────────────────────────────────────────────────

/** Parse "#RRGGBB" into a tuple of integers in [0, 255]. Assumes the input passed HEX_REGEX. */
function hexToRgb(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

/**
 * Linearize one sRGB component per W3C 2.1: the gamma-corrected curve that
 * undoes the perceptual encoding stored in HEX.
 */
function srgbToLinear(c: number): number {
  const v = c / 255;
  return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
}

/** Relative luminance of an sRGB colour, weighted by human eye sensitivity. */
function relativeLuminance([r, g, b]: [number, number, number]): number {
  return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
}

/**
 * Compute the WCAG 2.1 contrast ratio between two HEX colours.
 *
 * @param fgHex Foreground colour ("#RRGGBB").
 * @param bgHex Background colour ("#RRGGBB"). Defaults to white — the colour
 *              of the "Assinar" button label, which is hardcoded by the package.
 * @returns A ratio in [1, 21]. WCAG-AA passes ≥ 4.5 for normal text, ≥ 3.0 for
 *          large text. 1 means colours are identical (worst); 21 is the max
 *          (pure black vs pure white).
 *
 * Spec: https://www.w3.org/TR/WCAG21/#dfn-contrast-ratio
 *
 * Sanity checks (verified at U18.5 close):
 *   getWcagContrast('#000000', '#FFFFFF') === 21.00 (max)
 *   getWcagContrast('#154680', '#FFFFFF') ≈ 9.46    (Joshua brand — passes AA)
 *   getWcagContrast('#FFFF00', '#FFFFFF') ≈ 1.07    (yellow — fails AA)
 *   getWcagContrast('#154680', '#154680') === 1.00  (same colour — worst)
 */
function getWcagContrast(fgHex: string, bgHex: string = SIGNATURE_BUTTON_TEXT_COLOR): number {
  const Lfg = relativeLuminance(hexToRgb(fgHex));
  const Lbg = relativeLuminance(hexToRgb(bgHex));
  const [bright, dark] = Lfg > Lbg ? [Lfg, Lbg] : [Lbg, Lfg];
  return (bright + 0.05) / (dark + 0.05);
}

// ─── Component ──────────────────────────────────────────────────────────────

/** Maximum signature_logo size — keeps the PDF stamper memory bounded. */
const MAX_LOGO_BYTES = 5 * 1024 * 1024; // 5 MB

/** Tab content for the "Brand para assinatura" tab of the Empresa drawer. */
export default function BrandSignatureSetup({
  isEditing,
  tenantId,
  formData,
  tenantConfig,
  setTenantConfig,
}: BrandSignatureSetupProps) {
  const { getAuditFields } = useAuth();
  const { showSuccess, showError } = useToast();
  // The current brand_color stored in tenantConfig — the canonical source.
  const storedBrandColor = useMemo(() => {
    const branding = (tenantConfig?.branding ?? null) as { brand_color?: string | null } | null;
    return branding?.brand_color ?? null;
  }, [tenantConfig]);

  // Local input state, so a half-typed value (e.g. "#15") does not write to
  // tenantConfig and trigger a re-validate cascade on each keystroke.
  const [hexInput, setHexInput] = useState<string>(storedBrandColor ?? '');
  const [hexError, setHexError] = useState<string | null>(null);

  // Sync local state when the stored value changes from outside (initial load,
  // mode flip from edit→view→edit).
  useEffect(() => {
    setHexInput(storedBrandColor ?? '');
    setHexError(null);
  }, [storedBrandColor]);

  // ── signature_logo (entidade_file with papel='signature_logo') ──────────
  // Mirror Empresa.tsx loadLogo pattern: load the entidade_file row, sign the
  // storage path with a long TTL, surface the URL for preview. Upload/remove
  // commits happen inside the Avatar widget, which writes/soft-deletes the
  // entidade_file row directly.
  const [signatureLogoUrl, setSignatureLogoUrl] = useState<string | null>(null);
  const [signatureLogoLoading, setSignatureLogoLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!tenantId) {
      setSignatureLogoUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setSignatureLogoLoading(true);
      try {
        const { data } = await supabase
          .from('entidade_file')
          .select('file:file_id(nome_storage)')
          .eq('entidade', 'tenant')
          .eq('entidade_id', tenantId)
          .eq('papel', 'signature_logo')
          .is('deleted_at', null)
          .maybeSingle();
        const nomeStorage = (data as { file?: { nome_storage?: string } } | null)?.file?.nome_storage;
        if (nomeStorage) {
          const { data: urlData } = await supabase.storage
            .from('documento')
            .createSignedUrl(nomeStorage, 60 * 60 * 24 * 365);
          if (!cancelled) setSignatureLogoUrl(urlData?.signedUrl ?? null);
        } else if (!cancelled) {
          setSignatureLogoUrl(null);
        }
      } catch {
        if (!cancelled) setSignatureLogoUrl(null);
      } finally {
        if (!cancelled) setSignatureLogoLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  // U20 — escape valve flag for the "Aceitar direto sem assinatura" route.
  // Loaded on mount, flipped via direct UPDATE on tenant; fn_audit_fields
  // already records who flipped it and when (trg_tenant_audit).
  const [allowDirect, setAllowDirect] = useState<boolean | null>(null);
  const [allowDirectSaving, setAllowDirectSaving] = useState(false);

  useEffect(() => {
    if (!tenantId) {
      setAllowDirect(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('tenant')
          .select('allow_direct_acceptance')
          .eq('id', tenantId)
          .maybeSingle();
        if (error) throw error;
        if (!cancelled) {
          setAllowDirect(
            (data as { allow_direct_acceptance?: boolean } | null)?.allow_direct_acceptance ?? false,
          );
        }
      } catch {
        if (!cancelled) setAllowDirect(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  const handleAllowDirectToggle = useCallback(async (next: boolean) => {
    if (!tenantId || allowDirectSaving) return;
    const previous = allowDirect;
    setAllowDirect(next); // optimistic
    setAllowDirectSaving(true);
    try {
      // allow_direct_acceptance is a fresh U20 column not yet in generated
      // types — cast at the boundary. fn_audit_fields trigger handles the
      // audit log row automatically; updated_by lands via getAuditFields.
      const { error } = await supabase
        .from('tenant')
        .update({ allow_direct_acceptance: next, ...getAuditFields() } as never)
        .eq('id', tenantId);
      if (error) throw error;
      showSuccess({
        title: 'Política atualizada',
        description: next
          ? 'Aceite direto habilitado para este tenant.'
          : 'Aceite direto desabilitado. Todas as propostas exigirão assinatura.',
      });
    } catch (err) {
      setAllowDirect(previous); // rollback
      showError(getErrorMessage(err));
    } finally {
      setAllowDirectSaving(false);
    }
  }, [tenantId, allowDirect, allowDirectSaving, getAuditFields, showSuccess, showError]);

  // Hidden file input ref + uploading flag. Inline uploader mirrors the
  // file+entidade_file pattern from Avatar (apps/web/src/components/ui/Avatar/Avatar.tsx)
  // but skips the circular crop step: signature_logo is rectangular and must
  // keep its original aspect ratio for the PDF stamp.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadInProgress, setUploadInProgress] = useState<boolean>(false);

  const handleFileSelected = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same filename later
    if (!file || !tenantId) return;

    if (!file.type.startsWith('image/')) {
      showError({ title: 'Formato inválido', description: 'Envie PNG/JPG/WebP. Recomendado PNG com transparência.' });
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      showError({ title: 'Arquivo muito grande', description: 'Limite de 5 MB.' });
      return;
    }

    setUploadInProgress(true);
    try {
      const auditFields = getAuditFields();

      // Remove any existing signature_logo (storage + file + entidade_file) — same
      // cleanup order Avatar uses.
      const { data: existingRaw } = await supabase
        .from('entidade_file')
        .select('file_id, file:file_id(nome_storage)')
        .eq('entidade', 'tenant')
        .eq('entidade_id', tenantId)
        .eq('papel', 'signature_logo')
        .is('deleted_at', null)
        .maybeSingle();
      const existing = existingRaw as { file_id: string; file: { nome_storage: string } | null } | null;
      if (existing) {
        if (existing.file?.nome_storage) {
          await supabase.storage.from('documento').remove([existing.file.nome_storage]);
        }
        await supabase
          .from('entidade_file')
          .delete()
          .eq('file_id', existing.file_id)
          .eq('entidade', 'tenant')
          .eq('entidade_id', tenantId);
        await supabase.from('file').delete().eq('id', existing.file_id);
      }

      // Upload the original bytes, no rewrite. Storage path follows the
      // tenant_signature_logo/<uuid>.<ext> convention so the PDF stamper can
      // recognise the slot at a glance.
      const fileId = crypto.randomUUID();
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      const storagePath = `tenant_signature_logo/${fileId}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('documento')
        .upload(storagePath, file, { contentType: file.type, upsert: false });
      if (uploadError) throw uploadError;

      // Long-lived signed URL for the preview (matches Avatar's 1-year TTL).
      const { data: urlData, error: urlError } = await supabase.storage
        .from('documento')
        .createSignedUrl(storagePath, 60 * 60 * 24 * 365);
      if (urlError) throw urlError;

      // file row — audit triggers fill anything we miss, but we pass tenant_id +
      // created_by explicitly via auditFields so RLS-on-INSERT is satisfied.
      const filePayload = {
        id: fileId,
        nome_original: file.name,
        nome_storage: storagePath,
        mime_type: file.type,
        tamanho_bytes: file.size,
        descricao: 'Logo para documentos de assinatura',
        tipo: 'signature_logo',
        ...auditFields,
      };
      const { error: fileError } = await (
        supabase.from('file') as unknown as {
          insert: (p: Record<string, unknown>) => Promise<{ error: unknown }>;
        }
      ).insert(filePayload);
      if (fileError) throw fileError;

      const { error: efError } = await supabase
        .from('entidade_file')
        .insert({
          file_id: fileId,
          entidade: 'tenant',
          entidade_id: tenantId,
          papel: 'signature_logo',
          ordem: 0,
          created_by: auditFields.created_by,
        });
      if (efError) throw efError;

      setSignatureLogoUrl(urlData.signedUrl);
      showSuccess({ title: 'Logo enviado', description: 'O logo para documentos foi salvo.' });
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      setUploadInProgress(false);
    }
  }, [tenantId, getAuditFields, showSuccess, showError]);

  const handleSignatureLogoRemove = useCallback(async () => {
    if (!tenantId) return;
    setUploadInProgress(true);
    try {
      const { data: existingRaw } = await supabase
        .from('entidade_file')
        .select('file_id, file:file_id(nome_storage)')
        .eq('entidade', 'tenant')
        .eq('entidade_id', tenantId)
        .eq('papel', 'signature_logo')
        .is('deleted_at', null)
        .maybeSingle();
      const existing = existingRaw as { file_id: string; file: { nome_storage: string } | null } | null;
      if (existing) {
        if (existing.file?.nome_storage) {
          await supabase.storage.from('documento').remove([existing.file.nome_storage]);
        }
        await supabase
          .from('entidade_file')
          .delete()
          .eq('file_id', existing.file_id)
          .eq('entidade', 'tenant')
          .eq('entidade_id', tenantId);
        await supabase.from('file').delete().eq('id', existing.file_id);
      }
      setSignatureLogoUrl(null);
      showSuccess({ title: 'Logo removido' });
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      setUploadInProgress(false);
    }
  }, [tenantId, showSuccess, showError]);

  // Derived checklist state — matches the four predicates `fn_tenant_signature_ready` validates.
  const nomeOk           = Boolean(formData.nome?.trim());
  const headerLogoOk     = Boolean(formData.logo_url?.trim());
  const brandColorOk     = HEX_REGEX.test(storedBrandColor ?? '');
  const signatureLogoOk  = Boolean(signatureLogoUrl);
  const allReady         = nomeOk && headerLogoOk && brandColorOk && signatureLogoOk;

  // Persist a valid HEX to tenantConfig — called on blur or when the swatch
  // (native colour input) changes. Invalid values stay only in hexInput +
  // surface an inline error, never reach tenantConfig.
  const commitBrandColor = useCallback((next: string) => {
    const candidate = next.trim();
    if (!HEX_REGEX.test(candidate)) {
      setHexError('Use HEX 6 dígitos, ex.: #154680');
      return;
    }
    setHexError(null);
    setTenantConfig((prev) => {
      const branding = (prev?.branding ?? {}) as Record<string, unknown>;
      return { ...prev, branding: { ...branding, brand_color: candidate.toUpperCase() } };
    });
  }, [setTenantConfig]);

  // Contrast info for the editing preview. Recomputed on every hexInput change
  // so the user sees the badge react live as they type the colour.
  const contrast = useMemo(() => {
    if (!HEX_REGEX.test(hexInput)) return null;
    const ratio = getWcagContrast(hexInput);
    return { ratio, passes: ratio >= WCAG_AA_NORMAL };
  }, [hexInput]);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Section 1: status checklist */}
      <FieldsetCard
        title="Status da identidade para assinatura"
        style={{ marginBottom: 0 }}
      >
        <p style={{ ...DRAWER_STYLES.typography.label, marginBottom: '8px', color: 'var(--muted)' }}>
          O envio de propostas para assinatura digital exige os quatro itens abaixo configurados.
        </p>
        <ChecklistRow label="Nome da empresa"                ok={nomeOk}           hint="Razão social na aba Dados." />
        <ChecklistRow label="Logo para header (claro)"       ok={headerLogoOk}     hint="Provisionado pela equipe Joshua, usado sobre a cor da marca." />
        <ChecklistRow label="Logo para documentos (escuro)"  ok={signatureLogoOk}  hint="Você faz upload abaixo, usado nas páginas brancas do PDF." />
        <ChecklistRow label="Cor da marca (HEX)"             ok={brandColorOk}     hint="Editável abaixo." />
        <div style={{
          marginTop: '12px',
          padding: '10px 12px',
          borderRadius: '6px',
          background: allReady ? '#ecfdf5' : '#fef3c7',
          border: `1px solid ${allReady ? '#a7f3d0' : '#fde68a'}`,
          color: allReady ? '#065f46' : '#92400e',
          fontSize: '12px',
          fontWeight: 600,
        }}>
          {allReady
            ? '✓ Identidade pronta — esta empresa pode enviar propostas para assinatura.'
            : '⚠ Identidade incompleta — o botão de enviar para assinatura ficará desabilitado.'}
        </div>
      </FieldsetCard>

      {/* Section 2: header logo (read-only). Rendered over brand_color because
          that's where it actually shows up in the signature flow — over the
          "Assinar" button background and the public page header, both painted
          with brand_color. The PNG is typically white-on-transparent. */}
      <FieldsetCard title="Logo para header (claro)" style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{
            width: '160px',
            height: '120px',
            borderRadius: '8px',
            border: `1px solid ${headerLogoOk ? '#d1d5db' : '#fca5a5'}`,
            background: HEX_REGEX.test(storedBrandColor ?? '')
              ? (storedBrandColor as string)
              : '#154680',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
            padding: '12px',
          }}>
            {headerLogoOk ? (
              <img
                src={formData.logo_url ?? ''}
                alt="Logo claro da empresa"
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            ) : (
              <span style={{ fontSize: '11px', color: '#ffffff', textAlign: 'center', padding: '8px', opacity: 0.85 }}>
                Sem logo configurado
              </span>
            )}
          </div>
          <div style={{ flex: 1, fontSize: '12px', color: 'var(--muted)', lineHeight: 1.5 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '6px' }}>
              <Info size={14} style={{ color: '#3b82f6', flexShrink: 0, marginTop: '2px' }} />
              <span>
                Versão clara (branco em fundo transparente). Provisionado pela equipe Joshua no
                onboarding da empresa. Para alterar, fale com o suporte.
              </span>
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic', marginLeft: '20px' }}>
              Preview renderizado sobre a cor da marca — é como o logo aparece no botão de assinar
              e no header da página pública de aceite.
            </div>
          </div>
        </div>
      </FieldsetCard>

      {/* Section 3: signature_logo (editable). Inline rectangular uploader —
          no cropping, preserves the original aspect ratio. The PDF stamper
          (U13 assinatura-stamping Edge Function) reads the bytes as-is and
          places them on the white proposta/contrato page; rounding the image
          here would clip the brand asset. */}
      <FieldsetCard title="Logo para documentos (escuro)" style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div
              onClick={() => {
                if (isEditing && !uploadInProgress) fileInputRef.current?.click();
              }}
              style={{
                width: '200px',
                height: '120px',
                borderRadius: '8px',
                border: signatureLogoOk
                  ? '1px solid #d1d5db'
                  : `1px dashed ${isEditing ? '#3b82f6' : '#fca5a5'}`,
                background: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                padding: '12px',
                cursor: isEditing && !uploadInProgress ? 'pointer' : 'default',
                transition: 'border-color 0.15s ease',
              }}
            >
              {signatureLogoLoading || uploadInProgress ? (
                <Loader2 size={28} className="animate-spin" style={{ color: '#9ca3af' }} />
              ) : signatureLogoUrl ? (
                <img
                  src={signatureLogoUrl}
                  alt="Logo escuro para documentos"
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              ) : isEditing ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', color: '#3b82f6' }}>
                  <Upload size={24} />
                  <span style={{ fontSize: '11px', fontWeight: 500 }}>Clique para enviar PNG</span>
                </div>
              ) : (
                <span style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
                  Sem logo configurado
                </span>
              )}
            </div>
            {isEditing && signatureLogoUrl && !uploadInProgress && (
              <button
                type="button"
                onClick={handleSignatureLogoRemove}
                title="Remover logo"
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: '#ef4444',
                  color: '#ffffff',
                  border: '2px solid #ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                <Trash2 size={12} />
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleFileSelected}
              style={{ display: 'none' }}
            />
          </div>
          <div style={{ flex: 1, fontSize: '12px', color: 'var(--muted)', lineHeight: 1.5 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '6px' }}>
              <Info size={14} style={{ color: '#3b82f6', flexShrink: 0, marginTop: '2px' }} />
              <span>
                Versão escura (preto/cinza escuro em fundo transparente). Usada no carimbo do PDF de
                proposta e contrato. Recomendamos PNG com transparência. Tamanho máximo 5 MB.
              </span>
            </div>
            <div style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic', marginLeft: '20px' }}>
              Preview sobre fundo branco — é como o logo aparece nas páginas do PDF assinado.
              O retângulo de preview preserva o aspect ratio do arquivo original.
            </div>
          </div>
        </div>
      </FieldsetCard>

      {/* Section 5: política de aceite (U20 escape-valve toggle) */}
      <FieldsetCard title="Política de aceite" style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ ...DRAWER_STYLES.typography.label, margin: 0, marginBottom: '4px', color: '#111827', fontWeight: 600 }}>
              Permitir "Aceitar direto sem assinatura"
            </p>
            <p style={{ ...DRAWER_STYLES.typography.label, margin: 0, color: 'var(--muted)' }}>
              Quando ativo, o botão Aceitar oferece a opção de fechar a proposta
              sem coletar assinatura digital. Mantenha desligado para padronizar
              o aceite via assinatura. Toda alteração fica registrada no log de
              auditoria do tenant.
            </p>
          </div>
          <div style={{ flexShrink: 0, paddingTop: '2px' }}>
            <Switch
              checked={allowDirect === true}
              onChange={handleAllowDirectToggle}
              disabled={!isEditing || allowDirect === null || allowDirectSaving}
              activeLabel="Permitido"
              inactiveLabel="Bloqueado"
              showLabel
            />
          </div>
        </div>
      </FieldsetCard>

      {/* Section 3: brand color (editable) */}
      <FieldsetCard title="Cor da marca" style={{ marginBottom: 0 }}>
        <p style={{ ...DRAWER_STYLES.typography.label, marginBottom: '12px', color: 'var(--muted)' }}>
          HEX sólido de 6 dígitos (ex.: #154680). Usada no botão de assinar, no carimbo do PDF
          e na página pública de aceite. Gradientes não são aceitos.
        </p>

        {isEditing ? (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <input
              type="text"
              value={hexInput}
              onChange={(e) => {
                const next = e.target.value;
                setHexInput(next);
                if (hexError && HEX_REGEX.test(next.trim())) setHexError(null);
              }}
              onBlur={() => commitBrandColor(hexInput)}
              placeholder="#154680"
              maxLength={7}
              style={{
                ...DRAWER_COMPONENTS.input,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                textTransform: 'uppercase',
                width: '140px',
                flexShrink: 0,
              }}
            />
            <input
              type="color"
              value={HEX_REGEX.test(hexInput) ? hexInput : '#154680'}
              onChange={(e) => {
                const next = e.target.value.toUpperCase();
                setHexInput(next);
                commitBrandColor(next);
              }}
              style={{
                width: '40px',
                height: '40px',
                padding: 0,
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                background: 'transparent',
                flexShrink: 0,
              }}
              aria-label="Seletor visual de cor"
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              {hexError ? (
                <div style={{ fontSize: '12px', color: '#dc2626', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <X size={14} /> {hexError}
                </div>
              ) : contrast ? (
                <ContrastBadge ratio={contrast.ratio} passes={contrast.passes} />
              ) : (
                <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                  Informe um HEX válido para ver o contraste contra branco.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {brandColorOk ? (
              <>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '6px',
                  background: storedBrandColor ?? '#FFFFFF',
                  border: '1px solid #d1d5db',
                  flexShrink: 0,
                }} />
                <span style={{ ...DRAWER_STYLES.typography.value, fontFamily: 'ui-monospace, monospace' }}>
                  {storedBrandColor}
                </span>
              </>
            ) : (
              <span style={{ fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>
                Nenhuma cor configurada. Use o botão "Editar" para definir.
              </span>
            )}
          </div>
        )}
      </FieldsetCard>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

/** One row of the status checklist (✓/✗ + label + hint). */
function ChecklistRow({ label, ok, hint }: { label: string; ok: boolean; hint: string }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 0',
      borderBottom: '1px solid #f3f4f6',
    }}>
      <div style={{
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        background: ok ? '#10b981' : '#ef4444',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        {ok ? <Check size={12} color="white" strokeWidth={3} /> : <X size={12} color="white" strokeWidth={3} />}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '13px', fontWeight: 500, color: '#111827' }}>{label}</div>
        <div style={{ fontSize: '11px', color: 'var(--muted)' }}>{hint}</div>
      </div>
    </div>
  );
}

/** WCAG-AA contrast feedback badge — only shown when the HEX validates. */
function ContrastBadge({ ratio, passes }: { ratio: number; passes: boolean }) {
  const color = passes ? '#065f46' : '#b45309';
  const bg    = passes ? '#ecfdf5' : '#fef3c7';
  const Icon  = passes ? Check : AlertCircle;
  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 10px',
      borderRadius: '6px',
      background: bg,
      color,
      fontSize: '12px',
      fontWeight: 600,
    }}>
      <Icon size={14} />
      Contraste vs branco: {ratio.toFixed(2)}:1
      {passes
        ? ' (passa WCAG-AA)'
        : ` (abaixo de ${WCAG_AA_NORMAL}:1 — texto branco pode ficar ilegível)`}
    </div>
  );
}
